<template>
  <documents-metadata
    ref="documentMetadataRef"
    :fetch-metadata-and-field-infos="getDocumentMetadata"
    :update-metadata-function="updateDocumentMetadata"
    :can-edit-metadata="canEditMetadata"
  >
    <template #value-KKS="{ data }">
      <div class="kks-selector-contaner">
        <kks-selector
          ref="mainKksSelectorRef"
          :key="kksPickerRerenderKey"
          v-model="data.value"
          class="kks-selector"
          :busy="isLoading"
          :can-remove-last="false"
          :disabled="!canEditMetadata"
          :label="t('documentDetailView.kksTree')"
          :location="projectLocation!"
          @change="updateKks"
        />
        <div class="kks-buttons">
          <PVButton
            v-if="
              document.metadata.checkBotEstablishedKKS?.length &&
              canEditMetadata
            "
            class="show-kks-proposals-button"
            :size="'small'"
            @click="isShowingKksProposalsModal = true"
          >
            {{ t('documentDetailView.showKksProposals') }}
          </PVButton>
          <PVButton
            v-if="isFrame()"
            :size="'small'"
            class="show-kks-in-big-button"
            @click="openKksInBigModal()"
          >
            {{ t('documentDetailView.showKksInBig') }}
          </PVButton>
        </div>
      </div>
    </template>
  </documents-metadata>

  <modal-wrapper
    v-model:is-visible="isShowingKksProposalsModal"
    :display-footer="false"
    :size="ModalSize.L"
    :header="t('documentDetailView.kksProposalsModalTitle')"
  >
    <check-bot-established-kks-panel
      :file="document"
      :is-active="true"
      :is-validating-kks="isValidatingKks"
      :kks-validation-list="kksValidationList"
      :mode="ProjectType.SUPPLIER_PROJECT"
      @document-data-changed="handleCheckBotKksSelected"
    />
  </modal-wrapper>

  <modal-wrapper
    v-if="isFrame()"
    v-model:is-visible="isShowingKksInBigModal"
    :display-footer="false"
    :size="ModalSize.L"
    :header="t('documentDetailView.kksInBigModalTitle')"
    content-overflow-y="visible"
    :position="isFrame() ? 'top' : 'center'"
  >
    <kks-selector
      v-if="projectLocation && isShowingKksInBigModal"
      v-model="bigKksValue"
      :busy="isLoading"
      :can-remove-last="true"
      :label="t('documentDetailView.kksTree')"
      :location="projectLocation"
      @change="handleBigKksChange"
    />
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { useKksValidator } from '@/composables/useKksValidator';
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList, isFrame } from '@/helpers';
  import { MetadataUpdateService, ProjectDataService } from '@/services';
  import axios from 'axios';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IFileMetadataWithWithFieldsMetadataDto,
    ISupplierDocumentDetails,
    KKS_METADATA_FIELD,
    KksState,
    ProjectType,
    Role,
    Serialized,
  } from 'types';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { KksSelectorChangeEvent } from '../TreeSelect/KksSelector.types';
  import KksSelector from '../TreeSelect/KksSelector.vue';
  import { removeEmptyStrings } from 'utils';
  import DocumentsMetadata from './DocumentsMetadata.vue';
  import { executeBigSelection } from '@/utils/big.ts';
  import ModalWrapper from '@/components/ModalWrapper/ModalWrapper.vue';
  import { ModalSize } from '@/components/ModalWrapper/ModalWrapper.types';

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const props = defineProps<{
    projectId: string;
    document: Serialized<ISupplierDocumentDetails>;
  }>();

  const { t } = useI18n();
  const { isUserRole } = useRoles();
  const { createErrorToast } = useToast();
  const { kksValidationList, isValidatingKks, validateKks } = useKksValidator();

  const kksPickerRerenderKey = ref<number>(0);
  const isLoadingMetadata = ref(false);
  const projectLocation = ref<string | null>(null);
  const isShowingKksProposalsModal = ref<boolean>(false);
  const isShowingKksInBigModal = ref<boolean>(false);
  const bigKksValue = ref<string>('');
  const mainKksSelectorRef = ref<InstanceType<typeof KksSelector> | null>(null);
  const documentMetadataRef = ref<InstanceType<
    typeof DocumentsMetadata
  > | null>(null);

  const isLoading = computed(() => {
    return isLoadingMetadata.value || projectLocation.value === null;
  });

  const metadataUpdateService = new MetadataUpdateService(
    ProjectType.SUPPLIER_PROJECT,
  );
  const projectDataService = new ProjectDataService(
    ProjectType.SUPPLIER_PROJECT,
  );

  const canEditMetadata = computed(() => {
    return (
      props.document.canEditMetadata &&
      isUserRole([Role.Admin, Role.DocuManager])
    );
  });

  async function getDocumentMetadata(): Promise<IFileMetadataWithWithFieldsMetadataDto> {
    try {
      const response = await axios.post<IFileMetadataWithWithFieldsMetadataDto>(
        endpointsList.documentsUploads.getFileMetadataByIds(
          props.projectId,
          true,
        ),
        [props.document._id],
      );
      return response.data;
    } catch (error) {
      createErrorToast();
      throw error;
    }
  }

  async function updateDocumentMetadata(
    metadataField: string,
    fieldValue: string | string[],
  ): Promise<void> {
    await metadataUpdateService.updateMultiValueMetadataProperty({
      projectId: props.projectId,
      documentOid: props.document._id,
      metadataField,
      values: Array.isArray(fieldValue) ? fieldValue : [fieldValue],
    });
    emit('document-data-changed');
  }

  onMounted(getProjectLocation);
  async function getProjectLocation(): Promise<void> {
    projectLocation.value = await projectDataService.getProjectLocation(
      props.projectId,
    );
  }

  async function updateKks(e: KksSelectorChangeEvent): Promise<void> {
    try {
      isLoadingMetadata.value = true;
      const kks = e.target.value.map((kks) => kks.kks_trimmed);
      if (!canEditMetadata.value) return;

      if (!kks.length) {
        kksPickerRerenderKey.value++;
        createErrorToast(
          t('documentDetailView.errors.cannotRemoveLastValue.long'),
        );
        return;
      }
      updateDocumentMetadata(KKS_METADATA_FIELD, kks);
      executeBigSelection(kks);
    } catch {
      createErrorToast();
    } finally {
      isLoadingMetadata.value = false;
    }
  }

  async function handleCheckBotKksSelected(): Promise<void> {
    await documentMetadataRef.value?.getMetadataAndFieldInfos();
    kksPickerRerenderKey.value++;
    await validateKksAndCheckBotKks();
    emit('document-data-changed');
  }

  function handleBigKksChange(e: KksSelectorChangeEvent): void {
    const kks = e.target.value.map((k) => k.kks_trimmed);
    executeBigSelection(kks);
  }

  function openKksInBigModal(): void {
    isShowingKksInBigModal.value = true;
    const currentKks = mainKksSelectorRef.value?.getSelectedKks() ?? [];
    const kks = currentKks.map((k) => k.kks_trimmed);
    bigKksValue.value = kks.join(DEFAULT_METADATA_VALUES_SEPARATOR);
    executeBigSelection(kks);
  }

  watch([props.document.metadata, projectLocation], validateKksAndCheckBotKks, {
    deep: true,
    immediate: true,
  });
  async function validateKksAndCheckBotKks(): Promise<void> {
    const kks = getKksAndCheckBotKks();
    if (!kks?.length) return;
    if (!projectLocation.value) return;

    await validateKks(kks, projectLocation.value);
  }

  function getKksAndCheckBotKks(): string[] {
    const checkBotEstablishedKks =
      props.document.metadata.checkBotEstablishedKKS;
    const acceptedKks = props.document.metadata.KKS.split(
      DEFAULT_METADATA_VALUES_SEPARATOR,
    );

    return removeEmptyStrings([
      ...(acceptedKks ?? []),
      ...(checkBotEstablishedKks ?? []),
    ]);
  }
</script>

<style lang="scss" scoped>
  .kks-selector-contaner {
    display: flex;
    align-items: center;
    column-gap: 1rem;
    .kks-selector {
      flex: 1 1 auto;
      min-width: 0;
    }

    .kks-buttons {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .show-kks-proposals-button,
      .show-kks-in-big-button {
        white-space: nowrap;
        background-color: $primary-color;
      }
    }
  }

  .big-modal-hint {
    margin: 0 0 1rem 0;
    font-size: 0.875rem;
    color: var(--text-color-secondary);
  }
</style>
